import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { productCategories, allProducts } from '../../../data/productsData';

const ProductCategories = () => {
  const [selectedCategory, setSelectedCategory] = useState(productCategories[0].id);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter products based on search term
  const filteredProducts = searchTerm 
    ? allProducts.filter(product => 
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.subcategoryName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : null;

  const currentCategory = productCategories.find(cat => cat.id === selectedCategory);

  // Enhanced product modal with WhatsApp integration
  const ProductModal = ({ product, onClose }) => {
    if (!product) return null;

    const handleWhatsAppInquiry = () => {
      const message = `Hello! I'm interested in your ${product.name}. Could you please provide more information about pricing and availability?`;
      const whatsappUrl = `https://wa.me/2348012345678?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
    };

    return (
      <AnimatePresence>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="relative p-6 bg-gradient-to-r from-primary to-primary-dark text-white rounded-t-2xl">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors duration-200"
              >
                <Icon name="X" size={18} />
              </button>
              <div className="pr-8">
                <h3 className="text-xl font-bold mb-1">{product.name}</h3>
                <p className="text-primary-light text-sm">{product.subcategoryName}</p>
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              <p className="text-text-secondary leading-relaxed mb-6">
                {product.description}
              </p>

              {/* Specifications */}
              {product.specifications && (
                <div className="space-y-4 mb-6">
                  <h4 className="font-semibold text-primary text-lg">Specifications</h4>
                  <div className="grid grid-cols-1 gap-3">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key} className="flex justify-between items-center py-2 px-3 bg-surface-50 rounded-lg">
                        <span className="text-sm font-medium text-text-primary">{key}</span>
                        <span className="text-sm text-text-secondary font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col space-y-3">
                <button
                  onClick={handleWhatsAppInquiry}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-medium py-3 px-4 rounded-xl flex items-center justify-center space-x-2 transition-colors duration-200"
                >
                  <Icon name="MessageCircle" size={18} />
                  <span>Inquire via WhatsApp</span>
                </button>
                <button
                  onClick={onClose}
                  className="w-full bg-surface-100 hover:bg-surface-200 text-text-primary font-medium py-3 px-4 rounded-xl transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>
    );
  };

  return (
    <section className="py-16 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Search Bar */}
        <div className="mb-12">
          <div className="max-w-lg mx-auto relative">
            <input
              type="text"
              placeholder="Search products, categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-6 py-4 pl-14 border-2 border-surface-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-lg bg-white shadow-sm"
            />
            <Icon name="Search" size={22} className="absolute left-5 top-1/2 transform -translate-y-1/2 text-text-muted" />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-8 h-8 rounded-full bg-surface-100 hover:bg-surface-200 flex items-center justify-center transition-colors duration-200"
              >
                <Icon name="X" size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Category Navigation */}
        {!searchTerm && (
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-4">
              {productCategories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-8 py-4 rounded-2xl transition-all duration-300 flex items-center space-x-3 min-w-[200px] ${
                    selectedCategory === category.id
                      ? `bg-gradient-to-r ${category.color} text-white shadow-lg shadow-primary/25`
                      : 'bg-white text-text-secondary hover:bg-surface-50 border-2 border-surface-200 hover:border-surface-300'
                  }`}
                >
                  <Icon name={category.icon} size={20} />
                  <span className="font-semibold">{category.name}</span>
                </motion.button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {searchTerm && filteredProducts && (
          <div className="mb-8">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-primary mb-2">
                Search Results
              </h3>
              <p className="text-text-muted">
                Found {filteredProducts.length} products matching "{searchTerm}"
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <motion.div
                  key={`${product.categoryId}-${product.subcategoryId}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="bg-white rounded-2xl shadow-sm border border-surface-200 hover:shadow-lg hover:border-primary/20 transition-all duration-300 cursor-pointer group overflow-hidden"
                  onClick={() => setSelectedProduct(product)}
                >
                  {/* Product Card Content */}
                  <div className="p-6">
                    <div className="mb-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                          {product.categoryName}
                        </span>
                        <span className="px-3 py-1 bg-surface-100 text-text-muted text-xs font-medium rounded-full">
                          {product.subcategoryName}
                        </span>
                      </div>
                      <h4 className="font-bold text-primary text-lg mb-2 group-hover:text-accent transition-colors duration-300">
                        {product.name}
                      </h4>
                      <p className="text-sm text-text-secondary line-clamp-3 leading-relaxed">
                        {product.description}
                      </p>
                    </div>

                    {/* Quick Specs Preview */}
                    {product.specifications && (
                      <div className="space-y-2">
                        {Object.entries(product.specifications).slice(0, 2).map(([key, value]) => (
                          <div key={key} className="flex justify-between text-xs">
                            <span className="text-text-muted font-medium">{key}:</span>
                            <span className="text-text-primary font-semibold">{value}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* View Details Button */}
                    <div className="mt-4 pt-4 border-t border-surface-100">
                      <button className="w-full text-center text-primary font-medium text-sm hover:text-accent transition-colors duration-200 flex items-center justify-center space-x-2">
                        <Icon name="Eye" size={16} />
                        <span>View Details</span>
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}

        {/* Category Content */}
        {!searchTerm && currentCategory && (
          <div className="space-y-16">
            {/* Category Header */}
            <div className="text-center">
              <motion.div
                key={selectedCategory}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className={`inline-flex items-center space-x-4 px-8 py-4 rounded-2xl bg-gradient-to-r ${currentCategory.color} text-white mb-6 shadow-lg`}
              >
                <Icon name={currentCategory.icon} size={28} />
                <span className="text-xl font-bold">{currentCategory.name}</span>
              </motion.div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="text-text-secondary max-w-4xl mx-auto text-lg leading-relaxed"
              >
                {currentCategory.description}
              </motion.p>
            </div>

            {/* Subcategories */}
            {currentCategory.subcategories.map((subcategory, subIndex) => (
              <motion.div
                key={subcategory.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: subIndex * 0.1 }}
                className="space-y-8"
              >
                {/* Subcategory Header */}
                <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-6 bg-white rounded-2xl p-6 shadow-sm border border-surface-200">
                  <div className="relative w-20 h-20 md:w-24 md:h-24 rounded-2xl overflow-hidden shadow-md flex-shrink-0">
                    <Image
                      src={subcategory.image}
                      alt={subcategory.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-bold text-primary mb-2">{subcategory.name}</h3>
                    <p className="text-text-secondary text-lg leading-relaxed">{subcategory.description}</p>
                    <div className="mt-3">
                      <span className="inline-flex items-center px-3 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full">
                        {subcategory.products.length} Products Available
                      </span>
                    </div>
                  </div>
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {subcategory.products.map((product, productIndex) => (
                    <motion.div
                      key={productIndex}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, delay: (subIndex * 0.05) + (productIndex * 0.03) }}
                      className="bg-white rounded-2xl shadow-sm border border-surface-200 hover:shadow-lg hover:border-primary/20 transition-all duration-300 cursor-pointer group overflow-hidden"
                      onClick={() => setSelectedProduct(product)}
                    >
                      <div className="p-6">
                        {/* Product Header */}
                        <div className="mb-4">
                          <h4 className="font-bold text-primary text-lg mb-2 group-hover:text-accent transition-colors duration-300">
                            {product.name}
                          </h4>
                          <p className="text-sm text-text-secondary line-clamp-3 leading-relaxed">
                            {product.description}
                          </p>
                        </div>

                        {/* Key Specifications */}
                        {product.specifications && (
                          <div className="space-y-2 mb-4">
                            {Object.entries(product.specifications).slice(0, 3).map(([key, value]) => (
                              <div key={key} className="flex justify-between text-xs">
                                <span className="text-text-muted font-medium">{key}:</span>
                                <span className="text-text-primary font-semibold">{value}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Premium Badge & Action */}
                        <div className="flex items-center justify-between pt-4 border-t border-surface-100">
                          <span className="px-3 py-1 bg-accent/10 text-accent text-xs font-semibold rounded-full">
                            Premium Quality
                          </span>
                          <button className="text-primary hover:text-accent transition-colors duration-200 flex items-center space-x-1">
                            <Icon name="Eye" size={16} />
                            <span className="text-sm font-medium">Details</span>
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-20 bg-gradient-to-r from-primary to-primary-dark rounded-3xl p-8 md:p-12 text-center text-white"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Ready to Source Premium Products?
          </h3>
          <p className="text-primary-light mb-8 max-w-2xl mx-auto text-lg">
            Contact us today to discuss your requirements and get competitive quotes for our premium agricultural products.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary hover:bg-surface-50 font-semibold py-4 px-8 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2">
              <Icon name="Phone" size={18} />
              <span>Request Quote</span>
            </button>
            <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-8 rounded-xl transition-colors duration-200 flex items-center justify-center space-x-2">
              <Icon name="MessageCircle" size={18} />
              <span>WhatsApp Us</span>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Product Modal */}
      <ProductModal product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </section>
  );
};

export default ProductCategories;